// ============================================
// MoltiVerse v3 - 3D Galaxy Scene
// ============================================
// HYPER-OPTIMIZED for smooth 60fps at any scale

import { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import ForceGraph3D from 'react-force-graph-3d';
import * as THREE from 'three';
import { useUniverseStore } from '@/stores/universe-store';
import { SUBMOLT_COLORS, type Agent, type Connection, getNodeId } from '@/lib/types';

// ============ PERFORMANCE CONFIG ============
const PERF_CONFIG = {
  // Quality thresholds - adjusted for 20K+ agents
  MAX_NODES_FULL_QUALITY: 1000,    // Above this, reduce quality
  MAX_NODES_MEDIUM_QUALITY: 5000,  // Above this, minimal quality
  MAX_NODES_LOW_QUALITY: 15000,    // Above this, ultra minimal
  
  // Rendering limits - only used in pathfinder/timelapse
  MAX_LINKS_VISIBLE: 500,          // Only for focused agent
  MAX_LINKS_LOW: 200,              // Minimal
  
  // Geometry - use low segments for massive datasets
  SPHERE_SEGMENTS_HIGH: 5,
  SPHERE_SEGMENTS_MEDIUM: 3,
  SPHERE_SEGMENTS_LOW: 2,
  
  // Physics - quick stabilization
  WARMUP_TICKS: 50,                // Let it spread nicely
  COOLDOWN_TICKS: 20,              // Then freeze
  
  // Renderer
  DEVICE_PIXEL_RATIO: Math.min(window.devicePixelRatio, 1.5),
};

// ============ MATERIAL CACHE ============
const materialCache = new Map<string, THREE.MeshBasicMaterial>();
const geometryCache = new Map<number, THREE.SphereGeometry>();

function getCachedMaterial(colorHex: string, opacity: number = 0.9): THREE.MeshBasicMaterial {
  const key = `${colorHex}-${opacity}`;
  if (!materialCache.has(key)) {
    materialCache.set(key, new THREE.MeshBasicMaterial({
      color: new THREE.Color(colorHex),
      transparent: opacity < 1,
      opacity,
    }));
  }
  return materialCache.get(key)!;
}

function getCachedGeometry(size: number, segments: number): THREE.SphereGeometry {
  const key = Math.round(size * 10) + segments * 1000;
  if (!geometryCache.has(key)) {
    geometryCache.set(key, new THREE.SphereGeometry(size, segments, segments));
  }
  return geometryCache.get(key)!;
}

// ForceGraph3D instance type - using flexible type due to library's internal complexity
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ForceGraphInstance = any;

export function GalaxyScene() {
  const graphRef = useRef<ForceGraphInstance>(null);
  const [isReady, setIsReady] = useState(false);
  
  const {
    graphData,
    view,
    selectedAgent,
    agentConnections,
    agentInfluencers,
    agentInfluenced,
    focusAgent,
    toggle3rdLayer,
  } = useUniverseStore();

  // Determine quality level based on node count
  const qualityLevel = useMemo(() => {
    const nodeCount = graphData.nodes.length;
    if (nodeCount <= PERF_CONFIG.MAX_NODES_FULL_QUALITY) return 'high';
    if (nodeCount <= PERF_CONFIG.MAX_NODES_MEDIUM_QUALITY) return 'medium';
    if (nodeCount <= PERF_CONFIG.MAX_NODES_LOW_QUALITY) return 'low';
    return 'ultra-low';
  }, [graphData.nodes.length]);

  const sphereSegments = useMemo(() => {
    switch (qualityLevel) {
      case 'high': return PERF_CONFIG.SPHERE_SEGMENTS_HIGH;
      case 'medium': return PERF_CONFIG.SPHERE_SEGMENTS_MEDIUM;
      default: return PERF_CONFIG.SPHERE_SEGMENTS_LOW;
    }
  }, [qualityLevel]);

  const maxLinks = useMemo(() => {
    return qualityLevel === 'ultra-low' 
      ? PERF_CONFIG.MAX_LINKS_LOW 
      : PERF_CONFIG.MAX_LINKS_VISIBLE;
  }, [qualityLevel]);

  // ============ SETUP GRAPH ON MOUNT ============
  useEffect(() => {
    const graph = graphRef.current;
    if (!graph) return;

    // Wait for graph to initialize
    const timer = setTimeout(() => {
      // Configure forces for GALAXY effect - tight sphere with some spread
      graph.d3Force('charge')?.strength(-30);   // Weaker repulsion = tighter cluster
      graph.d3Force('link')?.distance(30);      // Shorter links
      graph.d3Force('center')?.strength(0.1);   // Strong center pull to keep galaxy shape
      
      // Add custom radial force to create spherical galaxy
      const d3 = (window as any).d3;
      if (d3 && graph.d3Force) {
        // Radial force - pulls nodes toward a sphere surface
        graph.d3Force('radial', d3.forceRadial?.(200, 0, 0, 0)?.strength(0.05));
      }
      
      // Slower velocity decay = smoother settling
      graph.d3VelocityDecay?.(0.3);
      
      // Configure camera controls
      const controls = graph.controls(); 
      if (controls) {
        controls.enableDamping = true;
        controls.dampingFactor = 0.1;
        controls.minDistance = 50;
        controls.maxDistance = 3000;
        controls.zoomSpeed = 1.2;
        controls.rotateSpeed = 0.8;
        controls.panSpeed = 0.8;
      }
      
      // PERF: Limit pixel ratio on renderer
      const renderer = graph.renderer?.();
      if (renderer) {
        renderer.setPixelRatio(PERF_CONFIG.DEVICE_PIXEL_RATIO);
      }
      
      // Set camera to see the whole galaxy
      graph.cameraPosition({ x: 0, y: 200, z: 600 });
      
      setIsReady(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // ============ FLY TO SELECTED AGENT ============
  useEffect(() => {
    if (!selectedAgent || !graphRef.current || !isReady) return;
    
    // Function to find and fly to node
    const flyToNode = () => {
      const graph = graphRef.current;
      if (!graph || typeof graph.graphData !== 'function') return false;
      
      // Get the node's actual position from ForceGraph's internal graph data
      // This is where the d3 simulation stores the x, y, z coordinates
      const graphNodes = graph.graphData()?.nodes || [];
      const node = graphNodes.find((n: any) => n.id === selectedAgent.id);
      
      if (!node || node.x === undefined) {
        return false; // Not ready yet
      }
      
      const targetX = node.x;
      const targetY = node.y || 0;
      const targetZ = node.z || 0;
      
      // Position camera to look at the agent from a good angle
      const distance = 150;
      graph.cameraPosition(
        { x: targetX, y: targetY + 50, z: targetZ + distance },  // Camera position (above and behind)
        { x: targetX, y: targetY, z: targetZ },                   // Look at the agent
        1000                                                       // Animation duration
      );
      return true;
    };
    
    // Try immediately
    if (flyToNode()) return;
    
    // Retry after simulation has time to position nodes
    let secondTimer: ReturnType<typeof setTimeout> | null = null;
    const retryTimer = setTimeout(() => {
      if (!flyToNode()) {
        // One more try after longer delay
        secondTimer = setTimeout(flyToNode, 500);
      }
    }, 200);
    
    return () => {
      clearTimeout(retryTimer);
      if (secondTimer) clearTimeout(secondTimer);
    };
  }, [selectedAgent, isReady]);
  
  // ============ CACHE GALAXY DATA ============
  const galaxyPositionsRef = useRef<Map<string, { x: number; y: number; z: number }>>(new Map());
  const cachedGalaxyNodesRef = useRef<Agent[]>([]);
  
  // Save galaxy positions when entering pathfinder and center camera
  useEffect(() => {
    if (view.mode === 'pathfinder' && graphRef.current) {
      // Cache current positions for instant restore
      graphData.nodes.forEach(node => {
        if (node.x !== undefined) {
          galaxyPositionsRef.current.set(node.id, {
            x: node.x,
            y: node.y || 0,
            z: node.z || 0,
          });
        }
      });
      
      // Center camera on the focused agent (which is pinned at origin)
      const graph = graphRef.current;
      if (typeof graph.cameraPosition === 'function') {
        setTimeout(() => {
          graph.cameraPosition(
            { x: 0, y: 80, z: 200 },  // Camera position (above and in front)
            { x: 0, y: 0, z: 0 },      // Look at origin (where focused agent is)
            800                         // Animation duration
          );
        }, 100);
      }
    }
  }, [view.mode, graphData.nodes]);
  
  // ============ ZOOM OUT ON EXIT PATHFINDER ============
  // Track previous mode to detect pathfinder exit
  const prevModeRef = useRef(view.mode);
  
  useEffect(() => {
    const wasPathfinder = prevModeRef.current === 'pathfinder';
    prevModeRef.current = view.mode;
    
    if (view.mode === 'galaxy' && graphRef.current && isReady) {
      const graph = graphRef.current;
      
      // CRITICAL: When exiting pathfinder, restore galaxy INSTANTLY
      if (wasPathfinder && typeof graph.graphData === 'function') {
        // STEP 1: Pause physics completely for instant transition
        graph.pauseAnimation?.();
        
        // STEP 2: Build nodes with frozen cached positions
        const frozenNodes = graphData.nodes.map(node => {
          const cached = galaxyPositionsRef.current.get(node.id);
          if (cached) {
            return { ...node, x: cached.x, y: cached.y, z: cached.z, fx: cached.x, fy: cached.y, fz: cached.z };
          }
          return node;
        });
        
        // STEP 3: Force update graph with frozen nodes, no links
        graph.graphData({ nodes: frozenNodes, links: [] });
        
        // STEP 4: Resume animation
        graph.resumeAnimation?.();
        
        // STEP 5: Zoom out to see full galaxy
        graph.cameraPosition(
          { x: 0, y: 300, z: 800 },
          { x: 0, y: 0, z: 0 },
          400  // Faster animation
        );
        
        // STEP 6: Unfreeze nodes after camera animation completes
        setTimeout(() => {
          const currentNodes = graph.graphData()?.nodes || [];
          currentNodes.forEach((node: any) => {
            node.fx = undefined;
            node.fy = undefined;
            node.fz = undefined;
          });
        }, 500);
        
        return; // Skip the rest for pathfinder exit
      }
      
      // Normal galaxy mode entry (not from pathfinder)
      graph.cameraPosition(
        { x: 0, y: 300, z: 800 },
        { x: 0, y: 0, z: 0 },
        600
      );
    }
  }, [view.mode, isReady, graphData.nodes]);

  // ============ FILTER DATA ============
  const filteredData = useMemo(() => {
    const { minKarmaFilter, minConnectionFilter, mode, focusedAgentId } = view;
    
    // In timelapse mode, limit nodes for smooth animation
    const MAX_TIMELAPSE_NODES = 800;
    const MAX_TIMELAPSE_LINKS = 1000;
    
    // PATHFINDER MODE: Show the focused agent's network with 4 layers
    if (mode === 'pathfinder' && focusedAgentId) {
      // Get the focused agent
      const focusedAgent = graphData.nodes.find(n => n.id === focusedAgentId);
      if (!focusedAgent) {
        return { nodes: [], links: [] };
      }
      
      // Build nodes from influencers + influenced (these come from API, not sample)
      const networkNodes: Agent[] = [focusedAgent];
      const layer1Ids = new Set<string>(); // Direct connections (1st layer)
      const layer2Ids = new Set<string>(); // 2nd layer
      const layer3Ids = new Set<string>(); // 3rd layer
      const addedIds = new Set([focusedAgentId]);
      
      // Add 1st layer - influencers (agents who reached out to focused agent)
      agentInfluencers.forEach(agent => {
        if (!addedIds.has(agent.id)) {
          networkNodes.push(agent as Agent);
          addedIds.add(agent.id);
          layer1Ids.add(agent.id);
        }
      });
      
      // Add 1st layer - influenced (agents the focused agent reached out to)
      agentInfluenced.forEach(agent => {
        if (!addedIds.has(agent.id)) {
          networkNodes.push(agent as Agent);
          addedIds.add(agent.id);
          layer1Ids.add(agent.id);
        }
      });
      
      // Extended mode: Find 2nd and 3rd layer connections from sampled graph data
      if (view.show3rdLayer) {
        const MAX_EXTENDED = 100;
        let addedExtended = 0;
        
        // Find 2nd layer - connections of layer1 nodes that exist in graphData
        graphData.links.forEach(link => {
          if (addedExtended >= MAX_EXTENDED) return;
          const sourceId = getNodeId(link.source);
          const targetId = getNodeId(link.target);
          
          // Check if this link connects a layer1 node to a new node
          if (layer1Ids.has(sourceId) && !addedIds.has(targetId)) {
            const node = graphData.nodes.find(n => n.id === targetId);
            if (node) {
              networkNodes.push(node);
              addedIds.add(targetId);
              layer2Ids.add(targetId);
              addedExtended++;
            }
          } else if (layer1Ids.has(targetId) && !addedIds.has(sourceId)) {
            const node = graphData.nodes.find(n => n.id === sourceId);
            if (node) {
              networkNodes.push(node);
              addedIds.add(sourceId);
              layer2Ids.add(sourceId);
              addedExtended++;
            }
          }
        });
        
        // Find 3rd layer - connections of layer2 nodes
        graphData.links.forEach(link => {
          if (addedExtended >= MAX_EXTENDED) return;
          const sourceId = getNodeId(link.source);
          const targetId = getNodeId(link.target);
          
          if (layer2Ids.has(sourceId) && !addedIds.has(targetId)) {
            const node = graphData.nodes.find(n => n.id === targetId);
            if (node) {
              networkNodes.push(node);
              addedIds.add(targetId);
              layer3Ids.add(targetId);
              addedExtended++;
            }
          } else if (layer2Ids.has(targetId) && !addedIds.has(sourceId)) {
            const node = graphData.nodes.find(n => n.id === sourceId);
            if (node) {
              networkNodes.push(node);
              addedIds.add(sourceId);
              layer3Ids.add(sourceId);
              addedExtended++;
            }
          }
        });
      }
      
      // Build links with degree markers
      const links: Array<Connection & { _degree: number }> = [];
      const addedLinks = new Set<string>();
      
      // Helper to track added links
      const linkKey = (s: string, t: string) => s < t ? `${s}-${t}` : `${t}-${s}`;
      
      // 1st degree links (focused <-> direct connections)
      const directConnections = agentConnections.direct || [];
      directConnections.forEach(c => {
        const sId = c.source as string;
        const tId = c.target as string;
        if (addedIds.has(sId) && addedIds.has(tId)) {
          links.push({ ...c, _degree: 1 });
          addedLinks.add(linkKey(sId, tId));
        }
      });
      
      // All other links between added nodes, with degree markers
      graphData.links.forEach(link => {
        const sourceId = getNodeId(link.source);
        const targetId = getNodeId(link.target);
        
        if (!addedIds.has(sourceId) || !addedIds.has(targetId)) return;
        if (addedLinks.has(linkKey(sourceId, targetId))) return;
        
        // Determine degree based on source/target layers (max 3 layers)
        let degree = 3;
        if (layer1Ids.has(sourceId) && layer1Ids.has(targetId)) degree = 2;
        else if ((layer1Ids.has(sourceId) && layer2Ids.has(targetId)) || 
                 (layer2Ids.has(sourceId) && layer1Ids.has(targetId))) degree = 2;
        else if (layer2Ids.has(sourceId) && layer2Ids.has(targetId)) degree = 3;
        
        links.push({ ...link, _degree: degree });
        addedLinks.add(linkKey(sourceId, targetId));
      });
      
      // Pin the focused agent to the center
      const nodesWithPositions = networkNodes.map(node => {
        if (node.id === focusedAgentId) {
          return { ...node, fx: 0, fy: 0, fz: 0 };
        }
        return node;
      });
      
      return { nodes: nodesWithPositions, links };
    }
    
    // GALAXY MODE: Show all agents in a spherical galaxy layout
    if (mode === 'galaxy') {
      // Find max karma for normalization
      const maxKarma = Math.max(...graphData.nodes.map(n => n.karma || 0), 1);
      
      // Filter and position nodes in a spherical galaxy
      const nodes = graphData.nodes
        .filter(node => {
          if ((node.karma || 0) < minKarmaFilter) return false;
          return true;
        })
        .map((node, index) => {
          // If node already has position from previous simulation, keep it
          if (node.x !== undefined && node.y !== undefined && node.z !== undefined) {
            return node;
          }
          
          // Create spherical galaxy distribution
          // Higher karma = closer to center (more prominent)
          const karmaRatio = Math.sqrt((node.karma || 0) / maxKarma);
          const baseRadius = 150; // Inner radius for high karma
          const maxRadius = 400;  // Outer radius for low karma
          const radius = baseRadius + (1 - karmaRatio) * (maxRadius - baseRadius);
          
          // Use golden ratio spiral for even distribution
          const goldenRatio = (1 + Math.sqrt(5)) / 2;
          const theta = 2 * Math.PI * index / goldenRatio;
          const phi = Math.acos(1 - 2 * (index + 0.5) / graphData.nodes.length);
          
          // Add some randomness for natural look
          const jitter = 0.3;
          const r = radius * (1 + (Math.random() - 0.5) * jitter);
          
          return {
            ...node,
            x: r * Math.sin(phi) * Math.cos(theta),
            y: r * Math.cos(phi) * 0.6, // Flatten slightly for disc effect
            z: r * Math.sin(phi) * Math.sin(theta),
          };
        });
      
      cachedGalaxyNodesRef.current = nodes;
      return { nodes, links: [] };
    }
    
    let nodes = graphData.nodes.filter(node => {
      if ((node.karma || 0) < minKarmaFilter) return false;
      return true;
    });
    
    // TIMELAPSE MODE: Limit for performance
    if (mode === 'timelapse' && nodes.length > MAX_TIMELAPSE_NODES) {
      nodes = nodes
        .sort((a, b) => (b.karma || 0) - (a.karma || 0))
        .slice(0, MAX_TIMELAPSE_NODES);
    }
    
    // Links only for timelapse
    let links: typeof graphData.links = [];
    if (mode === 'timelapse') {
      const nodeIds = new Set(nodes.map(n => n.id));
      links = graphData.links.filter(link => {
        const sourceId = getNodeId(link.source);
        const targetId = getNodeId(link.target);
        if (!nodeIds.has(sourceId) || !nodeIds.has(targetId)) return false;
        if ((link.weight || 1) < minConnectionFilter) return false;
        return true;
      });
      
      if (links.length > MAX_TIMELAPSE_LINKS) {
        links = links
          .sort((a, b) => (b.weight || 1) - (a.weight || 1))
          .slice(0, MAX_TIMELAPSE_LINKS);
      }
    }
    
    return { nodes, links };
  }, [graphData, view.minKarmaFilter, view.minConnectionFilter, view.mode, view.focusedAgentId, view.show3rdLayer, agentConnections, agentInfluencers, agentInfluenced, maxLinks]);

  // ============ NODE STYLING ============
  const getNodeColor = useCallback((node: Agent) => {
    const baseColor = SUBMOLT_COLORS[node.main_submolt] || SUBMOLT_COLORS.default;
    
    // In pathfinder mode, highlight connections
    if (view.mode === 'pathfinder' && view.focusedAgentId) {
      if (node.id === view.focusedAgentId) return '#ffffff';
      
      const isDirect = agentConnections.direct.some(
        c => c.source === node.id || c.target === node.id
      );
      if (isDirect) return baseColor;
      
      const isSecond = agentConnections.secondDegree.some(
        c => c.source === node.id || c.target === node.id
      );
      if (isSecond) return '#666666';
      
      return '#222222';
    }
    
    return baseColor;
  }, [view.mode, view.focusedAgentId, agentConnections]);

  const getNodeVal = useCallback((node: Agent) => {
    // Composite engagement score for realistic sizing
    const karma = node.karma || 0;
    const bonds = node.connection_count || 0;
    const posts = node.post_count || 0;
    
    // Weighted engagement score
    // Karma is the main driver, bonds show social impact, posts show activity
    const karmaScore = Math.log10(Math.max(karma, 1)) * 1.0;
    const bondScore = Math.log10(Math.max(bonds, 1)) * 0.6;
    const postScore = Math.log10(Math.max(posts, 1)) * 0.3;
    
    // Base size + engagement
    let size = 1.2 + karmaScore + bondScore + postScore;
    
    // Tier multipliers for visual distinction
    if (karma >= 100000) size *= 1.4;      // Top tier (100k+)
    else if (karma >= 10000) size *= 1.2;  // High tier (10k+)
    
    // Focused agent is bigger
    if (node.id === view.focusedAgentId) size *= 2.5;
    
    // Clamp size for performance
    return Math.min(Math.max(size, 1), 10);
  }, [view.focusedAgentId]);

  // ============ LINK STYLING ============
  // PERF: Pre-compute connection sets for O(1) lookups
  const connectionSets = useMemo(() => {
    const directSet = new Set<string>();
    const secondSet = new Set<string>();
    
    for (const c of agentConnections.direct) {
      directSet.add(`${c.source}::${c.target}`);
      directSet.add(`${c.target}::${c.source}`);
    }
    for (const c of agentConnections.secondDegree) {
      secondSet.add(`${c.source}::${c.target}`);
      secondSet.add(`${c.target}::${c.source}`);
    }
    
    return { directSet, secondSet };
  }, [agentConnections]);

  const getLinkColor = useCallback((link: Connection & { _degree?: number }) => {
    // Timelapse mode - cyan
    if (view.mode === 'timelapse') {
      return 'rgba(0, 200, 255, 0.6)';
    }
    
    // Pathfinder mode - color by connection degree (1-3 layers)
    if (view.mode === 'pathfinder' && view.focusedAgentId) {
      const degree = link._degree || 3;
      
      // 1st degree - bright gold (direct connections to focused)
      if (degree === 1) {
        return 'rgba(255, 200, 0, 0.9)';
      }
      
      // 2nd degree - purple
      if (degree === 2) {
        return 'rgba(180, 100, 255, 0.6)';
      }
      
      // 3rd degree - dim cyan
      return 'rgba(0, 200, 255, 0.35)';
    }
    
    // Galaxy mode - no links shown anyway
    return 'rgba(0, 200, 255, 0.5)';
  }, [view.mode, view.focusedAgentId]);

  const getLinkWidth = useCallback((link: Connection & { _degree?: number }) => {
    if (view.mode !== 'pathfinder' || !view.focusedAgentId) return 0.5;
    
    const degree = link._degree || 3;
    
    // Width by degree (1-3 layers only)
    if (degree === 1) return 1.5;
    if (degree === 2) return 0.8;
    return 0.4;
  }, [view.mode, view.focusedAgentId]);
  
  // ============ ADJUST FORCES FOR PATHFINDER MODE ============
  useEffect(() => {
    const graph = graphRef.current;
    if (!graph || !isReady) return;
    
    if (view.mode === 'pathfinder') {
      // Pathfinder: Moderate spacing for clearer visualization
      graph.d3Force('charge')?.strength(-200);   // Good repulsion
      graph.d3Force('link')?.distance(80);        // Medium links
      graph.d3Force('center')?.strength(0.03);   // Light centering
    } else {
      // Galaxy/Timelapse: Original compact settings
      graph.d3Force('charge')?.strength(-100);
      graph.d3Force('link')?.distance(50);
      graph.d3Force('center')?.strength(0.05);
    }
    
    // Reheat the simulation
    graph.d3ReheatSimulation?.();
  }, [view.mode, isReady]);

  // ============ CUSTOM NODE RENDERING ============
  // PERF: Use cached geometry and materials, simpler shading
  const nodeThreeObject = useCallback((node: Agent) => {
    let size = getNodeVal(node);
    const colorHex = getNodeColor(node);
    let opacity = 0.9;
    
    // In Pathfinder mode, emphasize network hierarchy by layer (smaller sizes)
    if (view.mode === 'pathfinder' && view.focusedAgentId) {
      if (node.id === view.focusedAgentId) {
        // Focused agent - medium-large, bright
        size *= 1.8;
        opacity = 1;
      } else {
        // Check if direct connection (1st layer)
        const isDirect = agentConnections.direct.some(
          c => c.source === node.id || c.target === node.id ||
               (typeof c.source === 'object' && (c.source as any).id === node.id) ||
               (typeof c.target === 'object' && (c.target as any).id === node.id)
        );
        if (isDirect) {
          // 1st layer - medium size
          size *= 1.2;
          opacity = 0.9;
        } else {
          // 2nd/3rd layers - smaller
          size *= 0.8;
          opacity = 0.6;
        }
      }
    }
    
    // Use cached geometry and material for performance
    const geometry = getCachedGeometry(size, sphereSegments);
    const material = getCachedMaterial(colorHex, opacity);
    
    return new THREE.Mesh(geometry, material);
  }, [getNodeVal, getNodeColor, view.mode, view.focusedAgentId, agentConnections, sphereSegments]);

  // ============ EVENT HANDLERS ============
  const handleNodeClick = useCallback((node: Agent, event: MouseEvent) => {
    event.stopPropagation();
    focusAgent(node.id);
  }, [focusAgent]);

  const handleBackgroundClick = useCallback(() => {
    if (view.focusedAgentId) {
      focusAgent(null);
    }
  }, [view.focusedAgentId, focusAgent]);

  return (
    <div className="w-full h-full relative">
      {/* Deep space background */}
      <div 
        className="absolute inset-0 -z-10"
        style={{
          background: `
            radial-gradient(ellipse at 30% 20%, rgba(20, 0, 40, 0.8) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, rgba(0, 20, 40, 0.8) 0%, transparent 50%),
            radial-gradient(ellipse at center, #0a0a1a 0%, #050510 100%)
          `,
        }}
      />
      
      {/* Stars overlay */}
      <div 
        className="absolute inset-0 -z-5 opacity-40"
        style={{
          backgroundImage: `
            radial-gradient(1px 1px at 20px 30px, white, transparent),
            radial-gradient(1px 1px at 40px 70px, rgba(255,255,255,0.8), transparent),
            radial-gradient(1px 1px at 50px 160px, rgba(255,255,255,0.6), transparent),
            radial-gradient(1px 1px at 90px 40px, white, transparent),
            radial-gradient(1px 1px at 130px 80px, rgba(255,255,255,0.7), transparent),
            radial-gradient(1px 1px at 160px 120px, white, transparent),
            radial-gradient(1px 1px at 200px 50px, rgba(255,255,255,0.5), transparent),
            radial-gradient(1px 1px at 220px 140px, white, transparent),
            radial-gradient(1px 1px at 260px 90px, rgba(255,255,255,0.8), transparent),
            radial-gradient(1px 1px at 300px 170px, white, transparent)
          `,
          backgroundSize: '300px 200px',
        }}
      />
      
      {/* 3D Force Graph - PERFORMANCE OPTIMIZED */}
      <ForceGraph3D
        ref={graphRef}
        graphData={filteredData}
        nodeId="id"
        nodeLabel={(node: Agent) => {
          const bondTier = (node.connection_count || 0) >= 100 ? '🏆' : 
                           (node.connection_count || 0) >= 50 ? '💎' : 
                           (node.connection_count || 0) >= 25 ? '🔥' : 
                           (node.connection_count || 0) >= 10 ? '⭐' : '•';
          const color = SUBMOLT_COLORS[node.main_submolt] || '#00ff88';
          return `
            <div style="background: linear-gradient(135deg, rgba(0,0,0,0.95), rgba(20,20,30,0.95)); padding: 12px 16px; border-radius: 10px; border: 1px solid ${color}; box-shadow: 0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${color}30; min-width: 180px;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <div style="width: 32px; height: 32px; border-radius: 8px; background: ${color}30; border: 2px solid ${color}; display: flex; align-items: center; justify-content: center; font-weight: bold; color: ${color};">
                  ${node.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style="color: #fff; font-weight: bold; font-size: 13px;">@${node.name}</div>
                  <div style="color: ${color}; font-size: 10px;">m/${node.main_submolt}</div>
                </div>
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.1);">
                <div>
                  <div style="color: #888; font-size: 9px; text-transform: uppercase;">Karma</div>
                  <div style="color: #fbbf24; font-weight: bold; font-size: 14px;">⚡ ${(node.karma || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div style="color: #888; font-size: 9px; text-transform: uppercase;">Bonds</div>
                  <div style="color: #a855f7; font-weight: bold; font-size: 14px;">${bondTier} ${(node.connection_count || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div style="color: #888; font-size: 9px; text-transform: uppercase;">Posts</div>
                  <div style="color: #22c55e; font-weight: bold; font-size: 14px;">📝 ${(node.post_count || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div style="color: #888; font-size: 9px; text-transform: uppercase;">Since</div>
                  <div style="color: #0ea5e9; font-size: 11px;">${node.first_seen ? new Date(node.first_seen).toLocaleDateString() : 'Unknown'}</div>
                </div>
              </div>
              <div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.1); text-align: center;">
                <span style="color: #666; font-size: 9px;">Click to explore network</span>
              </div>
            </div>
          `;
        }}
        nodeColor={getNodeColor as any}
        nodeVal={getNodeVal as any}
        nodeThreeObject={nodeThreeObject as any}
        nodeThreeObjectExtend={false}
        linkSource="source"
        linkTarget="target"
        linkColor={getLinkColor as any}
        linkWidth={view.mode === 'pathfinder' ? 1.5 : 0.3}
        linkOpacity={view.mode === 'pathfinder' ? 0.9 : 0.6}
        linkCurvature={0}
        linkDirectionalParticles={0}
        onNodeClick={handleNodeClick as any}
        onBackgroundClick={handleBackgroundClick}
        backgroundColor="rgba(0,0,0,0)"
        showNavInfo={false}
        enableNodeDrag={qualityLevel !== 'low' && view.mode !== 'timelapse'}
        enableNavigationControls={true}
        controlType="orbit"
        warmupTicks={view.mode === 'timelapse' ? 50 : view.mode === 'pathfinder' ? 30 : 0}
        cooldownTicks={view.mode === 'timelapse' ? 0 : view.mode === 'pathfinder' ? 10 : 0}
        d3AlphaDecay={view.mode === 'timelapse' ? 1 : view.mode === 'pathfinder' ? 0.1 : 1}
        d3VelocityDecay={view.mode === 'timelapse' ? 0.9 : view.mode === 'pathfinder' ? 0.4 : 0.9}
        rendererConfig={{
          antialias: false,
          alpha: true,
          powerPreference: 'high-performance',
          precision: 'lowp',
          stencil: false,
          depth: true,
        }}
      />
      
      {/* Ambient glow overlay */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0, 5, 15, 0.6) 100%)',
        }}
      />
      
      {/* Pathfinder mode header - consolidated controls */}
      {view.mode === 'pathfinder' && view.focusedAgentId && (
        <div className="absolute top-3 sm:top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 sm:gap-3">
          {/* Mode indicator */}
          <div className="bg-black/90 border border-yellow-500/50 rounded-lg px-2 sm:px-4 py-1.5 sm:py-2 text-yellow-400 text-xs sm:text-sm font-mono">
            🔍 <span className="hidden sm:inline">PATHFINDER — </span>Tap to exit
          </div>
          
          {/* Extended network toggle */}
          <button
            onClick={toggle3rdLayer}
            className="bg-black/90 border-2 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-mono font-bold transition-all hover:scale-105 cursor-pointer"
            style={{
              borderColor: view.show3rdLayer ? 'rgba(0, 200, 255, 0.7)' : 'rgba(100, 100, 100, 0.5)',
              color: view.show3rdLayer ? '#0ea5e9' : '#888',
              boxShadow: view.show3rdLayer ? '0 0 15px rgba(0, 200, 255, 0.3)' : 'none',
            }}
            title="Show 2nd & 3rd degree connections (if available in graph data)"
          >
            +2nd/3rd: {view.show3rdLayer ? 'ON' : 'OFF'}
          </button>
        </div>
      )}
    </div>
  );
}
